import {
  API_BASE_URL_OVERRIDE_KEY,
  isAllowedApiOverrideUrl,
  isAllowedDebugDeepLinkUrl,
  persistManualOverrideFromRawUrl,
} from '../utils/api-override';

// localStorage key holding a developer-selected API base URL. Only ever read on
// non-production builds (see getBaseUrl below). The debug menu owns the value
// and the mode/source keys that sit beside it.
export { API_BASE_URL_OVERRIDE_KEY, isAllowedApiOverrideUrl };

// Compile-time flag: the debug API override is available on any non-production
// build. `process.env.API_ENV` is inlined by webpack's DefinePlugin, so guarded
// blocks (and the debug menu) are dead-code-eliminated from the production
// bundle — production can neither read nor honour an override.
export const IS_DEBUG_API_OVERRIDE_ENABLED =
  !!process.env.API_ENV && process.env.API_ENV !== 'production';

function getDefaultBaseUrl(): string {
  if (process.env.API_ENV === 'production' || !process.env.API_ENV)
    return 'https://api.flex-appeal.nl';
  if (process.env.API_ENV === 'local')
    return 'http://localhost:8000';
  return 'https://api.oneteam-preview.io';
}

// Deep-link from the QA index (and similar): ?debug_api_base_url=<api preview>.
// Persists the same localStorage key as the debug menu, plus mode and a manual
// pin, then strips the param so a refresh does not re-process. Runs before
// BASE_URL is resolved below.
function applyApiBaseUrlOverrideFromQuery(): void {
  if (!IS_DEBUG_API_OVERRIDE_ENABLED) return;

  // The server sets `global.window = {}` (see source/server/index.js), so
  // require a real location.search + localStorage — browser-only.
  if (
    typeof window === 'undefined' ||
    typeof window.localStorage === 'undefined' ||
    typeof window.location === 'undefined' ||
    typeof window.location.search !== 'string' ||
    !window.location.search
  ) {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  const raw = params.get(API_BASE_URL_OVERRIDE_KEY);
  if (!raw) return;

  // A query param is applied with no health check, so it stays narrower than the
  // picker. The picker can still target production and other allowed hosts.
  if (!isAllowedDebugDeepLinkUrl(raw) || !persistManualOverrideFromRawUrl(raw)) return;

  params.delete(API_BASE_URL_OVERRIDE_KEY);
  const nextSearch = params.toString();
  const nextUrl =
    `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`;
  window.location.replace(nextUrl);
}

applyApiBaseUrlOverrideFromQuery();

export function getBaseUrl(): string {
  // Debug override — non-production builds only. The `process.env.API_ENV`
  // checks are inlined at build time, so this whole block is stripped from the
  // production bundle.
  if (
    process.env.API_ENV &&
    process.env.API_ENV !== 'production' &&
    // The server sets `global.window = {}` (see source/server/index.js), so
    // check for localStorage itself — the override is browser-only.
    typeof window !== 'undefined' &&
    typeof window.localStorage !== 'undefined'
  ) {
    const override = window.localStorage.getItem(API_BASE_URL_OVERRIDE_KEY);
    if (override) return override;
  }

  return getDefaultBaseUrl();
}

// Resolved once at module-init and captured by every direct importer. Selecting
// a debug environment persists the override and reloads the page so this — and
// all BASE_URL consumers — re-resolve against the chosen host.
export const BASE_URL = getBaseUrl();

// The env-derived host, ignoring any active debug override.
export const DEFAULT_BASE_URL = getDefaultBaseUrl();
